import { Check, Lock, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { validateStageProgression, type StageValidationResult } from "@/utils/stageValidation";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface WorkflowBarProps {
  currentStatus: string;
  jobType: string;
  jobId: string;
  onStatusChange: (status: string) => void;
}

interface WorkflowStage {
  id: string;
  stage_key: string;
  stage_name: string;
  stage_order: number;
  color: string;
}

export const WorkflowBar = ({ currentStatus, jobType, jobId, onStatusChange }: WorkflowBarProps) => {
  const [stages, setStages] = useState<WorkflowStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequirementsDialog, setShowRequirementsDialog] = useState(false);
  const [validationResult, setValidationResult] = useState<StageValidationResult | null>(null);
  const [targetStageName, setTargetStageName] = useState<string>('');

  useEffect(() => {
    loadWorkflowStages();
  }, [jobType]);

  // Normalize job type to workflow type (case-insensitive, handles variations)
  const normalizeType = (type: string): 'retail' | 'insurance' | 'commercial' => {
    const normalized = type?.toLowerCase().trim();
    if (normalized === 'retail' || normalized === 'residential') return 'retail';
    if (normalized === 'insurance') return 'insurance';
    return 'commercial';
  };

  const loadWorkflowStages = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("org_id")
        .eq("id", user.id)
        .single();

      if (!profile?.org_id) return;

      // Map job type to workflow type with normalization
      const workflowType = normalizeType(jobType);

      // Load workflow and stages
      const { data: workflow } = await supabase
        .from('workflows')
        .select(`
          id,
          workflow_stages (*)
        `)
        .eq('org_id', profile.org_id)
        .eq('workflow_type', workflowType)
        .maybeSingle();

      if (workflow?.workflow_stages) {
        const sortedStages = (workflow.workflow_stages as any[]).sort((a, b) => 
          a.stage_order - b.stage_order
        );
        setStages(sortedStages);
      } else {
        // Use default stages from useWorkflows.ts defaults
        const defaultStages = getDefaultStagesForType(workflowType);
        setStages(defaultStages);
      }
    } catch (error) {
      console.error('Error loading workflow stages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Default stages matching useWorkflows.ts structure
  const getDefaultStagesForType = (workflowType: 'retail' | 'insurance' | 'commercial'): WorkflowStage[] => {
    const defaults = {
      retail: [
        { id: '1', stage_key: 'new', stage_name: 'Lead', stage_order: 1, color: '#3b82f6' },
        { id: '2', stage_key: 'contacted', stage_name: 'Contacted', stage_order: 2, color: '#a855f7' },
        { id: '3', stage_key: 'scheduled', stage_name: 'Appointment Set', stage_order: 3, color: '#f97316' },
        { id: '4', stage_key: 'in_progress', stage_name: 'In Progress', stage_order: 4, color: '#06b6d4' },
        { id: '5', stage_key: 'proposed', stage_name: 'Proposal Sent', stage_order: 5, color: '#22c55e' },
        { id: '6', stage_key: 'completed', stage_name: 'Closed Won', stage_order: 6, color: '#10b981' },
      ],
      insurance: [
        { id: '1', stage_key: 'new', stage_name: 'Lead', stage_order: 1, color: '#3b82f6' },
        { id: '2', stage_key: 'contacted', stage_name: 'Inspection/Claim Filed', stage_order: 2, color: '#a855f7' },
        { id: '3', stage_key: 'scheduled', stage_name: 'Adjuster Meeting', stage_order: 3, color: '#f97316' },
        { id: '4', stage_key: 'approved', stage_name: 'Claim Approved', stage_order: 4, color: '#8b5cf6' },
        { id: '5', stage_key: 'inspected', stage_name: 'Proposal Sent', stage_order: 5, color: '#06b6d4' },
        { id: '6', stage_key: 'in_progress', stage_name: 'In Progress', stage_order: 6, color: '#22c55e' },
        { id: '7', stage_key: 'completed', stage_name: 'Closed Won', stage_order: 7, color: '#10b981' },
      ],
      commercial: [
        { id: '1', stage_key: 'new', stage_name: 'Lead', stage_order: 1, color: '#3b82f6' },
        { id: '2', stage_key: 'contacted', stage_name: 'Site Visit/Inspection', stage_order: 2, color: '#a855f7' },
        { id: '3', stage_key: 'scheduled', stage_name: 'Bidding/Tender', stage_order: 3, color: '#f59e0b' },
        { id: '4', stage_key: 'inspected', stage_name: 'Proposal Submitted', stage_order: 4, color: '#06b6d4' },
        { id: '5', stage_key: 'approved', stage_name: 'Contract Signed', stage_order: 5, color: '#8b5cf6' },
        { id: '6', stage_key: 'in_progress', stage_name: 'In Progress', stage_order: 6, color: '#22c55e' },
        { id: '7', stage_key: 'completed', stage_name: 'Closed Won', stage_order: 7, color: '#10b981' },
      ],
    };
    return defaults[workflowType];
  };

  // Find current stage index based on status
  const getCurrentStageIndex = () => {
    const index = stages.findIndex(s => s.stage_key === currentStatus);
    return index >= 0 ? index : 0;
  };

  const currentStageIndex = getCurrentStageIndex();

  const handleStageClick = async (stage: WorkflowStage, index: number) => {
    // Can always go backwards
    if (index <= currentStageIndex) {
      onStatusChange(stage.stage_key);
      return;
    }

    // Can only progress to next stage
    if (index > currentStageIndex + 1) {
      toast.error('Complete current stage before skipping ahead');
      return;
    }

    // Validate requirements for target stage
    const validation = await validateStageProgression(jobId, stage.stage_key);
    
    if (validation.canProgress) {
      onStatusChange(stage.stage_key);
      toast.success(`Moved to ${stage.stage_name}`);
    } else {
      setValidationResult(validation);
      setTargetStageName(stage.stage_name);
      setShowRequirementsDialog(true);
    }
  };

  if (loading || stages.length === 0) {
    return (
      <div className="border-b border-border/50 bg-background">
        <div className="container mx-auto px-4 sm:px-6 py-3">
          <div className="text-center text-xs text-muted-foreground">Loading workflow...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="border-y border-border/60 bg-muted/10">
        <div className="container mx-auto px-4 sm:px-6 py-3">
          <div className="rounded-xl border border-border/70 bg-background/95 px-3 py-3 shadow-sm">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
                    Workflow
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Stage {currentStageIndex + 1} of {stages.length}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Tap a stage to move forward or back.
                </p>
              </div>

              <div className="overflow-x-auto md:overflow-visible">
                <div className="flex min-w-max gap-2 md:min-w-0 md:flex-wrap">
                {stages.map((stage, index) => {
                  const isCompleted = index < currentStageIndex;
                  const isCurrent = index === currentStageIndex;
                  const isBlocked = index > currentStageIndex + 1;
                  const stageColor = stage.color || "#3b82f6";

                  let style: React.CSSProperties = {
                    borderColor: `${stageColor}40`,
                    backgroundColor: `${stageColor}12`,
                    color: stageColor,
                  };

                  if (isCurrent) {
                    style = {
                      backgroundColor: stageColor,
                      borderColor: stageColor,
                      color: "#ffffff",
                      boxShadow: `0 10px 24px -14px ${stageColor}`,
                    };
                  } else if (isCompleted) {
                    style = {
                      backgroundColor: `${stageColor}20`,
                      borderColor: `${stageColor}5E`,
                      color: stageColor,
                    };
                  } else if (isBlocked) {
                    style = {
                      borderColor: `${stageColor}24`,
                      backgroundColor: `${stageColor}0A`,
                      color: stageColor,
                      opacity: 0.45,
                    };
                  }

                  return (
                    <button
                      key={stage.id}
                      onClick={() => handleStageClick(stage, index)}
                      disabled={isBlocked}
                      title={stage.stage_name}
                      style={style}
                      className={cn(
                        "inline-flex min-w-[132px] items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-[11px] font-semibold transition-all md:flex-1",
                        isCurrent && "shadow-sm",
                        isBlocked && "cursor-not-allowed",
                        !isCurrent && !isBlocked && "hover:-translate-y-0.5 hover:shadow-sm"
                      )}
                    >
                      {isCompleted ? (
                        <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} style={{ color: isCurrent ? "#ffffff" : stageColor }} />
                      ) : isBlocked ? (
                        <Lock className="h-3 w-3 shrink-0" />
                      ) : null}
                      <span className="whitespace-nowrap">{stage.stage_name}</span>
                    </button>
                  );
                })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showRequirementsDialog} onOpenChange={setShowRequirementsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Requirements Not Met
            </DialogTitle>
            <DialogDescription>
              Complete the following requirements before moving to <strong>{targetStageName}</strong>:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {validationResult?.completedRequirements && validationResult.completedRequirements.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-green-600 dark:text-green-400">
                  ✓ Completed
                </h4>
                {validationResult.completedRequirements.map((req, i) => (
                  <div key={i} className="text-sm text-muted-foreground pl-4">
                    • {req}
                  </div>
                ))}
              </div>
            )}
            {validationResult?.missingRequirements && validationResult.missingRequirements.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-destructive">
                  ✗ Missing Requirements
                </h4>
                {validationResult.missingRequirements.map((req, i) => (
                  <div key={i} className="text-sm pl-4 flex items-start gap-2">
                    <span className="text-destructive mt-0.5">•</span>
                    <span>{req}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
